import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, Modal } from 'react-bootstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';

import DropZone from 'components/shared/Dropzone';
import {
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  PRODUCT_ENRICHMENT_RESOURCES_UPLOAD_PRODUCT_URLS,
} from 'lib/networking/endpoints';
import { ProductEnrichmentResourcesFileType } from 'lib/enums/ProductEnrichmentResourcesFileType';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import usePost from 'lib/hooks/usePost';
import useGet from 'lib/hooks/useGet';
import EntityType, { ENTITY_TYPE_OPTIONS } from 'lib/enums/EntityType';

const PRODUCT_ENRICHMENT_RESOURCES_URLS_FOLDER = 'product_enrichment_resources/urls';

function AddProductURLsModal({ show, onHide, refetch }) {
  const [entityType, setEntityType] = useState({});
  const [manufacturer, setManufacturer] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [file, setFile] = useState(null);

  const { data: { data: globalVendors } = {} } = useGet(GLOBAL_VENDORS_FOR_DROPDOWN);

  const entityNames = useMemo(() => {
    if (entityType.value === EntityType.DISTRIBUTOR && globalVendors) {
      return globalVendors.map(vendor => ({
        value: vendor.id,
        label: vendor.name,
      }));
    }
    return [];
  }, [entityType, globalVendors]);

  const { postData: uploadProductURLs, loading: isSubmitting } = usePost(
    PRODUCT_ENRICHMENT_RESOURCES_UPLOAD_PRODUCT_URLS,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Product URLs upload created successfully',
      }).then(() => onHide());
      refetch();
    },
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          `An error occurred while creating the product URLs upload` +
          `upload: ${error?.response?.data?.message}`,
      });
    },
  );

  const onSubmit = () => {
    const data = {
      manufacturer_id: entityType.value === EntityType.MANUFACTURER ? manufacturer?.value : null,
      vendor_id: entityType.value === EntityType.DISTRIBUTOR ? vendor?.value : null,
      file_type: ProductEnrichmentResourcesFileType.PRODUCT_URLS,
      upload_location_url: file?.s3_path,
    };
    uploadProductURLs(data);
  };

  const handleReset = () => {
    setEntityType({});
    setManufacturer(null);
    setVendor(null);
    setFile(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleReset} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Product URLs</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={onSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Entity Type</Form.Label>
            <Select
              value={entityType}
              options={ENTITY_TYPE_OPTIONS}
              onChange={e => setEntityType(e || {})}
              isSearchable
              isClearable
            />
          </Form.Group>
          {entityType.value && (
            <Form.Group className="mb-3">
              <Form.Label>
                {entityType.value === EntityType.MANUFACTURER ? 'Manufacturer' : 'Distributor'}
              </Form.Label>
              {entityType.value === EntityType.MANUFACTURER ? (
                <SearchableScrollableSelect
                  onChange={setManufacturer}
                  fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
                  optionMapper={manufacturer => ({
                    value: manufacturer.id,
                    label: manufacturer.name,
                  })}
                  autoFocus={false}
                  placeholder="Select Manufacturer"
                  value={manufacturer}
                />
              ) : (
                <Select
                  value={vendor}
                  options={entityNames}
                  onChange={e => setVendor(e)}
                  isSearchable
                  isClearable
                />
              )}
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>CSV File with Product URLs</Form.Label>
            <DropZone
              text="Drag and drop the CSV file here, or click to select the file"
              onUploadSuccess={newUrls => setFile(newUrls?.[0])}
              filePath={PRODUCT_ENRICHMENT_RESOURCES_URLS_FOLDER}
              fileUrls={file ? [file.presigned_url] : []}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleReset} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Uploading...' : 'Upload'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

AddProductURLsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
};

export default AddProductURLsModal;
